/* PALISADE CUSTOM — cart-drawer selling-plan switching, both directions.
 *
 *   one-time      → subscription   the "Upgrade to a Subscription" pill
 *   subscription  → any frequency  the plan select (snippets/palisade-cart-plan)
 *   subscription  → one-time       the same select's "One-time" option
 *
 * The last two did not exist before: a shopper who picked a subscription had no
 * way back to a one-time purchase from the cart.
 *
 * Delegated document-level listener so it survives the drawer's Section
 * Rendering re-renders. On click: POST /cart/change.js with the line's Skio
 * selling plan, then re-fetch the cart-drawer section and swap the drawer's
 * inner node in place (the dialog stays open; Horizon's declarative on:click
 * handlers keep working because they resolve at event time).
 *
 * Accessibility contract (WCAG 2.4.3 / 4.1.3): the pill is guarded with
 * aria-disabled (not `disabled`, which would eject focus mid-request); after
 * the swap, focus moves to the drawer heading and the change is announced via
 * a polite live region; failures surface in the row's error container.
 */
const SECTION_ID = 'cart-drawer-section';

/* Which input modality drove the last interaction, so the post-swap focus can be
 * silent for a mouse and visible for a keyboard. Both listeners are capturing and
 * passive: they only observe, and must see the event even if a component below
 * stops it propagating.
 *
 * Tab is not special-cased — any keydown counts. A shopper arrowing through the
 * select's options is using the keyboard just as much as one who tabbed to it, and
 * both should get the ring back on the control that replaces it.
 */
let keyboardModality = false;
document.addEventListener('keydown', () => { keyboardModality = true; }, { capture: true, passive: true });
document.addEventListener('pointerdown', () => { keyboardModality = false; }, { capture: true, passive: true });

/* PALISADE CUSTOM — a POINTER press on the frequency selector marks it quiet.
 *
 * Chrome matches :focus-visible on a <select> that is clicked, not only one that
 * is tabbed to, because clicking it opens a listbox. So the plum ring appeared
 * around the pill every time a shopper opened it with the mouse — read as a
 * doubled border against the pill's own plum hairline.
 *
 * Marked here rather than suppressed in CSS so KEYBOARD focus keeps its ring,
 * which WCAG 2.4.7 requires. The keydown handler below clears the marker, so
 * tabbing to the same control still shows it.
 */
document.addEventListener(
  'pointerdown',
  (event) => {
    const select = event.target?.closest?.('.palisade-cart-plan__select');
    if (select) select.setAttribute('data-pc-quiet-focus', '');
  },
  { capture: true, passive: true }
);

/* The quiet-focus marker is cleared the moment the shopper touches a key, so the
 * ring returns on the very next keyboard interaction rather than staying suppressed
 * until the element is replaced again. */
document.addEventListener(
  'keydown',
  (event) => {
    const quiet = event.target?.closest?.('[data-pc-quiet-focus]');
    if (quiet) quiet.removeAttribute('data-pc-quiet-focus');
  },
  { capture: true }
);

function announce(message) {
  let region = document.getElementById('palisade-cart-upsell-status');
  if (!region) {
    region = document.createElement('div');
    region.id = 'palisade-cart-upsell-status';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.className = 'visually-hidden';
    // Outside .cart-drawer__inner so the section swap never destroys it.
    const dialog = document.querySelector('#cart-drawer dialog') || document.body;
    dialog.appendChild(region);
  }
  region.textContent = '';
  // Re-set on a tick so repeat messages are re-announced.
  requestAnimationFrame(() => {
    region.textContent = message;
  });
}

function showRowError(button, line) {
  const container = document.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
  const text = document.querySelector(`[ref="cartItemError-${line}"]`);
  if (container && text) {
    text.textContent = button.dataset.errorMessage || 'Could not update this item. Please try again.';
    container.classList.remove('hidden');
  }
}

/* Change a line's selling plan and re-render the drawer.
 *
 * `sellingPlan` is passed through to the cart API VERBATIM and must be either a
 * plan id or the empty string. Do not coerce it with Number(): '' becomes 0, and
 * 0 is rejected with 422 "Cannot apply selling plan to variant" — verified
 * against this store. '' (and null) are the forms that clear a plan.
 *
 * Shared by the upgrade pill and the plan select so the two directions cannot
 * drift apart: same guard, same error surface, same swap, same focus contract.
 */
async function applyPlanChange(control, sellingPlan, successMessage) {
  const line = Number(control.dataset.line);
  // Quantity read at EVENT time from the row's input — the render-time
  // data-quantity snapshot can be stale after a quantity change in flight.
  const row = control.closest('tr') || control.closest('.cart-items__table-row');
  const quantityInput = row && row.querySelector('input[name="quantity"]');
  const quantity = Number(
    (quantityInput && quantityInput.value) || control.dataset.quantity || 1
  );

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity, selling_plan: sellingPlan }),
    });
    if (!response.ok) throw new Error(`cart/change ${response.status}`);
  } catch (error) {
    console.warn('[palisade-cart-upsell]', error);
    showRowError(control, line);
    announce(control.dataset.errorMessage || 'Could not update this item.');
    control.removeAttribute('aria-disabled');
    return;
  }

  await refreshDrawer(line, successMessage);
}

/* Re-render the cart drawer and land focus deliberately.
 *
 * Extracted so the plan change and the product swap share ONE implementation:
 * they have identical post-write obligations (never report failure once the cart
 * has changed, fall back to a reload if the section render is unavailable, move
 * focus because the operated control is destroyed by the swap), and two copies
 * of that contract would drift.
 */
async function refreshDrawer(line, successMessage) {
  // The cart change SUCCEEDED — from here on, a failed drawer re-render must
  // never report failure (the line already changed); fall back to a hard refresh
  // so the visible cart matches reality.
  try {
    // Re-render the drawer contents via the Section Rendering API.
    const sectionsResponse = await fetch(
      `${window.Shopify?.routes?.root || '/'}?sections=${SECTION_ID}`
    );
    if (!sectionsResponse.ok) throw new Error(`sections ${sectionsResponse.status}`);
    const sections = await sectionsResponse.json();
    const html = sections[SECTION_ID];
    if (html) {
      const fresh = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('.cart-drawer__inner');
      const current = document.querySelector('#cart-drawer .cart-drawer__inner');
      if (fresh && current) {
        current.replaceWith(fresh);
        /* The control that was operated is destroyed by the swap, so focus has to
           land somewhere deliberate (WCAG 2.4.3). Prefer whichever control now
           occupies that line — the select stays a select when only the frequency
           changed, and becomes the upgrade pill when the line went back to
           one-time — so a shopper can keep adjusting without hunting for it.
           The heading is the fallback for a line that no longer exists. */
        const successor =
          document.querySelector(`#cart-drawer [data-pc-plan][data-line="${line}"]`) ||
          document.querySelector(`#cart-drawer [data-pc-upsell][data-line="${line}"]`) ||
          document.getElementById('cart-drawer-heading');
        if (successor) {
          if (successor.id === 'cart-drawer-heading') successor.setAttribute('tabindex', '-1');
          /* Focus still MOVES for everyone — the drawer is a modal dialog, so
             letting focus fall to <body> would drop it outside the dialog's focus
             scope. What is suppressed is the visible RING, and only for pointer
             users: `:focus-visible` matches on programmatic focus for these
             controls, so a mouse-driven change was drawing a plum ring 2px outside
             the pill's own plum border, which read as a doubled border rather than
             as focus. Keyboard users keep the ring — removing it outright would
             break WCAG 2.4.7. */
          if (!keyboardModality) successor.setAttribute('data-pc-quiet-focus', '');
          successor.focus();
        }
        announce(successMessage);
        return;
      }
    }
    // Section render unavailable → hard refresh keeps the cart correct.
    window.location.reload();
  } catch (error) {
    console.warn('[palisade-cart-upsell]', error);
    window.location.reload();
  }
}

/* Swap this line for a DIFFERENT product's plan (1-pack <-> 3-pack).
 *
 * A selling plan carries no quantity and /cart/change.js carries no variant, so
 * moving between the 1-month plan and the 3-pack is two writes, not one. They are
 * ordered ADD FIRST, then remove:
 *
 *   add fails    -> nothing was destroyed; the shopper still has their line.
 *   remove fails -> the shopper has both lines, which is visible and fixable,
 *                   rather than an empty cart they have to rebuild.
 *
 * The reverse order is shorter but its failure mode is losing the line entirely.
 *
 * The old line is removed by its `key`, not its index: an add can renumber lines,
 * and a stale index would remove somebody else's row.
 */
async function applyProductSwap(control, variantId, sellingPlan, successMessage) {
  const line = Number(control.dataset.line);
  const row = control.closest('tr') || control.closest('.cart-items__table-row');
  const key = row && row.dataset.key;
  const root = window.Shopify?.routes?.root || '/';

  if (!key) {
    console.warn('[palisade-cart-upsell] no line key; refusing a swap that could remove the wrong row');
    showRowError(control, line);
    control.removeAttribute('aria-disabled');
    return;
  }

  try {
    const added = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          sellingPlan === ''
            ? { id: variantId, quantity: 1 }
            : { id: variantId, quantity: 1, selling_plan: sellingPlan },
        ],
      }),
    });
    if (!added.ok) throw new Error(`cart/add ${added.status}`);
  } catch (error) {
    console.warn('[palisade-cart-upsell]', error);
    showRowError(control, line);
    announce(control.dataset.errorMessage || 'Could not update this item.');
    control.removeAttribute('aria-disabled');
    return;
  }

  /* The add SUCCEEDED. From here the cart has already changed, so nothing below
     may report failure to the shopper — a failed remove leaves a duplicate line,
     which the re-render shows them, and a failed re-render falls back to reload. */
  try {
    await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
  } catch (error) {
    console.warn('[palisade-cart-upsell] swap added but old line remains', error);
  }

  await refreshDrawer(line, successMessage);
}

/* One-time → subscription (the upgrade pill). */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-pc-upsell]');
  if (!button || button.getAttribute('aria-disabled') === 'true') return;
  // Skip while Horizon has the cart locked mid-update (stale line indices).
  if (button.closest('.cart-items-disabled')) return;
  event.preventDefault();

  button.setAttribute('aria-disabled', 'true');

  /* The featured offer may live on another product (the 3-pack), in which case
     the pill is a product swap, not a plan change — same split as the select. */
  const upsellVariant = button.dataset.pcVariant;
  const message = button.dataset.successMessage || 'Item upgraded to a subscription.';
  if (upsellVariant) {
    await applyProductSwap(
      button,
      Number(upsellVariant),
      Number(button.dataset.sellingPlan),
      message
    );
    return;
  }

  await applyPlanChange(button, Number(button.dataset.sellingPlan), message);
});

/* Subscription → another frequency, or back to one-time (the plan select).
 *
 * `change`, not `input`: a native select fires change once the shopper has
 * committed, whereas `input` also fires while arrow-keying through the options —
 * which would post a cart change per option passed over.
 */
document.addEventListener('change', async (event) => {
  const select = event.target.closest('[data-pc-plan]');
  if (!select || select.getAttribute('aria-disabled') === 'true') return;
  if (select.closest('.cart-items-disabled')) return;

  const value = select.value;
  select.setAttribute('aria-disabled', 'true');

  /* An option carrying data-pc-variant targets ANOTHER product (1-pack <-> 3-pack),
     which /cart/change.js cannot express — it takes line, quantity and selling_plan
     only, never a variant. Those go through the add-then-remove path instead. */
  const chosen = select.options[select.selectedIndex];
  const targetVariant = chosen && chosen.dataset.pcVariant;
  if (targetVariant) {
    await applyProductSwap(
      select,
      Number(targetVariant),
      /* '' stays '' — Number('') is 0, and 0 is the 422 documented on
         applyPlanChange. An empty value here means "switch product AND drop the
         subscription", which the add below expresses by omitting the key. */
      value === '' ? '' : Number(value),
      value === ''
        ? select.dataset.oneTimeMessage || 'Switched to a one-time purchase.'
        : select.dataset.planMessage || 'Delivery frequency updated.'
    );
    return;
  }

  await applyPlanChange(
    select,
    value === '' ? '' : Number(value),
    value === ''
      ? select.dataset.oneTimeMessage || 'Switched to a one-time purchase.'
      : select.dataset.planMessage || 'Delivery frequency updated.'
  );
});
